/**
 * SidePanel Component
 *
 * Reusable side panel/drawer component with animations and accessibility.
 * Follows design system from mockups with consistent styling.
 *
 * Usage:
 *   <SidePanel isOpen={isOpen} onClose={handleClose} title="Settings" position="right">
 *     <SidePanel.Body>
 *       Panel content here
 *     </SidePanel.Body>
 *     <SidePanel.Footer>
 *       <Button onClick={handleSave}>Save</Button>
 *     </SidePanel.Footer>
 *   </SidePanel>
 */

import React, { useEffect, useRef } from 'react';
import { theme } from '@/styles/theme';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';

export interface SidePanelProps {
  /** Whether panel is open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Panel title */
  title?: string;
  /** Panel position */
  position?: 'left' | 'right';
  /** Panel width */
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Show close button */
  showCloseButton?: boolean;
  /** Close on backdrop click */
  closeOnBackdrop?: boolean;
  /** Close on escape key */
  closeOnEscape?: boolean;
  /** Panel content */
  children: React.ReactNode;
  /** Additional classes */
  className?: string;
  /** Force full screen on mobile (default: true) */
  fullScreenOnMobile?: boolean;
}

export const SidePanel: React.FC<SidePanelProps> & {
  Body: typeof SidePanelBody;
  Footer: typeof SidePanelFooter;
} = ({
  isOpen,
  onClose,
  title,
  position = 'right',
  width = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
  className = '',
  fullScreenOnMobile = true,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const panel = panelRef.current;
    if (!panel) return;

    const focusableElements = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    panel.addEventListener('keydown', handleTab as any);
    firstElement?.focus();

    return () => {
      panel.removeEventListener('keydown', handleTab as any);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Determine if panel should be full screen
  const shouldBeFullScreen = fullScreenOnMobile && isMobile;
  const shouldBeWider = isTablet;

  // Width styles - responsive
  const getWidthClass = () => {
    if (shouldBeFullScreen || width === 'full') {
      return 'w-full';
    }
    if (shouldBeWider) {
      // On tablet, use larger sizes but cap at screen width
      const tabletWidths = {
        sm: 'w-80 max-w-[90vw]',
        md: 'w-96 max-w-[90vw]',
        lg: 'w-[32rem] max-w-[90vw]',
        xl: 'w-[40rem] max-w-[90vw]',
        full: 'w-full',
      };
      return tabletWidths[width];
    }
    // Desktop sizes
    const widthStyles = {
      sm: 'w-80',
      md: 'w-96',
      lg: 'w-[32rem]',
      xl: 'w-[40rem]',
      full: 'w-full',
    };
    return widthStyles[width];
  };

  // Position styles
  const positionStyles = {
    left: 'left-0',
    right: 'right-0',
  };

  // Animation styles - different for mobile full screen
  const getAnimationClass = () => {
    if (shouldBeFullScreen) {
      // Slide up animation for full screen mobile
      return isOpen ? 'animate-slideInUp' : 'animate-slideOutDown';
    }
    // Regular side slide animation
    const animationStyles = {
      left: isOpen ? 'animate-slideInLeft' : 'animate-slideOutLeft',
      right: isOpen ? 'animate-slideInRight' : 'animate-slideOutRight',
    };
    return animationStyles[position];
  };

  // Backdrop styles
  const backdropStyles = `
    fixed inset-0 z-[1040]
    bg-black/50
    backdrop-blur-sm
    animate-fadeIn
  `;

  // Panel container styles - responsive
  const panelStyles = `
    fixed ${shouldBeFullScreen ? 'inset-0' : `top-0 ${positionStyles[position]}`}
    ${shouldBeFullScreen ? '' : 'h-full'} ${getWidthClass()}
    bg-card-light dark:bg-card-dark
    shadow-2xl
    z-[1050]
    flex flex-col
    ${getAnimationClass()}
    ${shouldBeFullScreen ? 'rounded-none' : ''}
  `;

  // Header styles - responsive padding
  const headerStyles = `
    flex items-center justify-between
    px-4 py-3 md:px-6 md:py-5
    border-b border-line dark:border-border-dark
    flex-shrink-0
    ${shouldBeFullScreen ? 'sticky top-0 bg-card-light dark:bg-card-dark z-10' : ''}
  `;

  // Close button styles - touch optimized
  const closeButtonStyles = `
    p-2.5 rounded-lg
    text-ink-3 hover:text-ink-2
   
    hover:bg-inset
    transition-colors duration-200
    touch-manipulation
    min-w-[44px] min-h-[44px]
    flex items-center justify-center
  `;

  return (
    <>
      <div
        className={backdropStyles}
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`${panelStyles} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'sidepanel-title' : undefined}
      >
        {(title || showCloseButton) && (
          <div className={headerStyles}>
            {title && (
              <h2
                id="sidepanel-title"
                className="text-xl font-semibold text-text-primaryLight dark:text-text-primaryDark"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={closeButtonStyles}
                aria-label="Close panel"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {children}
      </div>
    </>
  );
};

// SidePanel Body subcomponent
export interface SidePanelBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidePanelBody: React.FC<SidePanelBodyProps> = ({
  children,
  className = '',
  ...props
}) => {
  const bodyStyles = `
    flex-1 overflow-y-auto
    px-4 py-3 md:px-6 md:py-4
    overscroll-contain
  `;

  return (
    <div className={`${bodyStyles} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

// SidePanel Footer subcomponent
export interface SidePanelFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Alignment of footer content */
  align?: 'left' | 'center' | 'right' | 'between';
}

const SidePanelFooter: React.FC<SidePanelFooterProps> = ({
  children,
  align = 'right',
  className = '',
  ...props
}) => {
  const alignStyles = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  const footerStyles = `
    flex items-center gap-2 md:gap-3
    px-4 py-3 md:px-6 md:py-4
    border-t border-line dark:border-border-dark
    flex-shrink-0
    ${alignStyles[align]}
    pb-safe
  `;

  return (
    <div className={`${footerStyles} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

// Attach subcomponents
SidePanel.Body = SidePanelBody;
SidePanel.Footer = SidePanelFooter;

// Export default
export default SidePanel;
